import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LazyImage } from '@/components/ui/LazyImage';
import { Card } from '@/components/ui/card';
import { MapPin } from 'lucide-react';
import { fmtEUR } from '@/lib/utils';

const PLACEHOLDER_IMAGE = 'https://customer-assets.emergentagent.com/job_ee642069-4fc1-425d-bfbc-e67ee20a2646/artifacts/vxtmarer_ChatGPT%20Image%202.%20M%C3%A4rz%202026%2C%2012_35_03.png';

// 2-letter ISO country code → emoji flag
function flagEmoji(country) {
  if (!country) return '';
  const code = country.trim().toUpperCase().slice(0, 2);
  if (!/^[A-Z]{2}$/.test(code)) return '';
  return String.fromCodePoint(...code.split('').map((c) => 127397 + c.charCodeAt(0)));
}

export const SimilarMaterialsSection = ({ items }) => {
  const { t } = useTranslation();
  if (!items?.length) return null;

  return (
    <section className="mt-16 pt-8 border-t border-slate-200 dark:border-slate-800" data-testid="similar-materials-section">
      <h2 className="font-manrope text-xl md:text-2xl font-bold tracking-tight mb-6">
        {t('material.similar.heading', 'Ähnliche Materialien')}
      </h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {items.map((m) => {
          const flag = flagEmoji(m.site_country);
          return (
            <Link
              key={m.material_id}
              to={`/material/${m.material_id}`}
              className="block group"
              data-testid={`similar-material-card-${m.material_id}`}
            >
              <Card className="overflow-hidden border-slate-200 dark:border-slate-800 hover:border-orange-400 dark:hover:border-orange-500 transition-colors h-full flex flex-col">
                <div className="aspect-[4/3] bg-slate-100 dark:bg-slate-800 overflow-hidden">
                  <LazyImage
                    src={m.image_url || PLACEHOLDER_IMAGE}
                    alt={m.common_title}
                    className="w-full h-full group-hover:scale-[1.02] transition-transform"
                    fallbackSrc={PLACEHOLDER_IMAGE}
                  />
                </div>
                <div className="p-3 flex-1 flex flex-col gap-1">
                  <h3 className="font-semibold text-sm line-clamp-2">{m.common_title}</h3>
                  {m.manufacturer_name && (
                    <p className="text-xs text-muted-foreground line-clamp-1">{m.manufacturer_name}</p>
                  )}
                  {m.quantity_range && (
                    <p className="text-xs text-slate-600 dark:text-slate-300">{m.quantity_range}</p>
                  )}
                  <div className="flex items-center justify-between mt-auto pt-2">
                    {m.list_price_kEUR != null ? (
                      <span className="text-orange-500 font-semibold text-sm">€{fmtEUR(m.list_price_kEUR)}</span>
                    ) : (
                      <span className="text-xs text-muted-foreground">{t('material.priceOnRequest', 'Preis auf Anfrage')}</span>
                    )}
                    {flag && (
                      <span className="inline-flex items-center gap-1 text-xs text-muted-foreground" title={m.site_country}>
                        <MapPin className="h-3 w-3" /> <span>{flag}</span>
                      </span>
                    )}
                  </div>
                </div>
              </Card>
            </Link>
          );
        })}
      </div>
    </section>
  );
};
